import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const SchedulingForm = ({ 
  selectedDate, 
  selectedTime, 
  onScheduleInterview, 
  onCancel,
  editingInterview = null 
}) => {
  const [formData, setFormData] = useState({
    candidateId: '',
    interviewerId: '',
    type: 'video',
    duration: 60,
    location: '',
    meetingLink: '',
    notes: '',
    date: new Date().toISOString().split('T')[0], // Set today's date by default
    time: ''
  });

  const [conflicts, setConflicts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Mock data
  const candidates = [
    { value: 'c1', label: 'Sarah Johnson', description: 'Senior Frontend Developer' },
    { value: 'c2', label: 'Michael Chen', description: 'Full Stack Engineer' },
    { value: 'c3', label: 'Emily Rodriguez', description: 'UX/UI Designer' },
    { value: 'c4', label: 'David Thompson', description: 'Backend Developer' },
    { value: 'c5', label: 'Lisa Wang', description: 'Product Manager' }
  ];

  const interviewers = [
    { value: 'i1', label: 'John Smith', description: 'Senior Technical Lead' },
    { value: 'i2', label: 'Maria Garcia', description: 'HR Manager' },
    { value: 'i3', label: 'Robert Wilson', description: 'Engineering Manager' },
    { value: 'i4', label: 'Jennifer Lee', description: 'Product Director' },
    { value: 'i5', label: 'Alex Kumar', description: 'Senior Developer' }
  ];

  const interviewTypes = [
    { value: 'phone', label: 'Phone Interview', description: 'Audio-only conversation' },
    { value: 'video', label: 'Video Interview', description: 'Video conference call' },
    { value: 'in-person', label: 'In-Person Interview', description: 'Face-to-face meeting' }
  ];

  const durations = [
    { value: 30, label: '30 minutes', description: 'Quick screening' },
    { value: 45, label: '45 minutes', description: 'Standard interview' },
    { value: 60, label: '1 hour', description: 'Detailed interview' },
    { value: 90, label: '1.5 hours', description: 'Technical assessment' },
    { value: 120, label: '2 hours', description: 'Panel interview' }
  ];

  useEffect(() => {
    if (selectedDate && selectedTime) {
      setFormData(prev => ({
        ...prev,
        date: selectedDate?.toISOString()?.split('T')?.[0],
        time: selectedTime
      }));
    }
  }, [selectedDate, selectedTime]);

  useEffect(() => {
    if (editingInterview) {
      const interviewDate = new Date(editingInterview.dateTime);
      setFormData({
        candidateId: editingInterview?.candidateId,
        interviewerId: editingInterview?.interviewerId,
        type: editingInterview?.type,
        duration: editingInterview?.duration,
        location: editingInterview?.location || '',
        meetingLink: editingInterview?.meetingLink || '',
        notes: editingInterview?.notes || '',
        date: interviewDate?.toISOString()?.split('T')?.[0],
        time: interviewDate?.toTimeString()?.substring(0, 5)
      });
    }
  }, [editingInterview]);

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      
      // Auto-generate meeting link when type changes to video and no link exists
      if (field === 'type' && value === 'video' && !prev.meetingLink) {
        newData.meetingLink = `https://meet.google.com/${Math.random().toString(36).substring(2, 15)}`;
      }
      
      // Clear type-specific fields when changing type
      if (field === 'type') {
        if (value !== 'video') {
          newData.meetingLink = '';
        }
        if (value !== 'in-person') {
          newData.location = '';
        }
      }
      
      return newData;
    });

    // Check for conflicts when key fields change
    if (['interviewerId', 'date', 'time', 'duration'].includes(field)) {
      checkConflicts({ ...formData, [field]: value });
    }
  };

  const checkConflicts = (data) => {
    // Mock conflict detection
    const mockConflicts = [];
    
    if (data?.interviewerId === 'i1' && data?.time === '14:00') {
      mockConflicts?.push({
        type: 'interviewer',
        message: 'John Smith has another interview at 2:00 PM',
        suggestion: 'Try 3:00 PM or 4:00 PM'
      });
    }
    
    if (data?.type === 'in-person' && data?.time === '10:00') {
      mockConflicts?.push({
        type: 'room',
        message: 'Conference Room A is booked at 10:00 AM',
        suggestion: 'Conference Room B is available'
      });
    }

    setConflicts(mockConflicts);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const interviewData = {
        ...formData,
        dateTime: new Date(`${formData.date}T${formData.time}`),
        id: editingInterview?.id || `int_${Date.now()}`,
        candidateName: candidates?.find(c => c?.value === formData?.candidateId)?.label,
        interviewerName: interviewers?.find(i => i?.value === formData?.interviewerId)?.label,
        status: 'scheduled'
      };

      onScheduleInterview(interviewData);
      
      // Reset form if not editing
      if (!editingInterview) {
        setFormData({
          candidateId: '',
          interviewerId: '',
          type: 'video',
          duration: 60,
          location: '',
          meetingLink: '',
          notes: '',
          date: formData?.date,
          time: formData?.time
        });
      }
    } catch (error) {
      console.error('Error scheduling interview:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = () => {
    const hasBasicFields = formData?.candidateId && 
                          formData?.interviewerId && 
                          formData?.date && 
                          formData?.time;
    
    // Additional validation based on interview type
    const hasTypeSpecificFields = 
      (formData?.type === 'in-person' && formData?.location?.trim()) ||
      (formData?.type === 'video' && formData?.meetingLink?.trim()) ||
      formData?.type === 'phone'; // Phone interviews don't need extra fields
    
    return hasBasicFields && hasTypeSpecificFields;
  };

  return (
    <div className="bg-surface rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="CalendarPlus" size={18} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {editingInterview ? 'Edit Interview' : 'Schedule Interview'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {selectedDate && `${selectedDate?.toLocaleDateString()} ${selectedTime || ''}`}
            </p>
          </div>
        </div>
        {editingInterview && (
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel Edit
          </Button>
        )}
      </div>
      {/* Conflicts Alert */}
      {conflicts?.length > 0 && (
        <div className="mb-6 p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="AlertTriangle" size={18} color="var(--color-warning)" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-warning mb-2">Scheduling Conflicts Detected</h4>
              {conflicts?.map((conflict, index) => (
                <div key={index} className="text-sm text-warning mb-1">
                  <p>{conflict?.message}</p>
                  <p className="text-xs opacity-80">Suggestion: {conflict?.suggestion}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Candidate Selection */}
        <Select
          label="Select Candidate"
          description="Choose the candidate for this interview"
          required
          searchable
          options={candidates}
          value={formData?.candidateId}
          onChange={(value) => handleInputChange('candidateId', value)}
        />

        {/* Interviewer Selection */}
        <Select
          label="Assign Interviewer"
          description="Select the interviewer for this session"
          required
          searchable
          options={interviewers}
          value={formData?.interviewerId}
          onChange={(value) => handleInputChange('interviewerId', value)}
        />

        {/* Date and Time */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Interview Date"
            type="date"
            required
            value={formData?.date}
            onChange={(e) => handleInputChange('date', e?.target?.value)}
          />
          <Input
            label="Interview Time"
            type="time"
            required
            value={formData?.time}
            onChange={(e) => handleInputChange('time', e?.target?.value)}
          />
        </div>

        {/* Interview Type and Duration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Interview Type"
            description="Choose the interview format"
            required
            options={interviewTypes}
            value={formData?.type}
            onChange={(value) => handleInputChange('type', value)}
          />
          <Select
            label="Duration"
            description="Expected interview length"
            required
            options={durations}
            value={formData?.duration}
            onChange={(value) => handleInputChange('duration', value)}
          />
        </div>

        {/* Conditional Fields */}
        {formData?.type === 'in-person' && (
          <Input
            label="Location"
            type="text"
            placeholder="Conference Room A, Building 1"
            description="Physical meeting location"
            required
            value={formData?.location}
            onChange={(e) => handleInputChange('location', e?.target?.value)}
          />
        )}

        {formData?.type === 'video' && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Meeting Link *
            </label>
            <div className="flex space-x-2">
              <Input
                type="url"
                placeholder="https://zoom.us/j/123456789"
                description="Video conference meeting URL"
                required
                value={formData?.meetingLink}
                onChange={(e) => handleInputChange('meetingLink', e?.target?.value)}
                className="flex-1"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const meetingId = Math.random().toString(36).substring(2, 15);
                  handleInputChange('meetingLink', `https://meet.google.com/${meetingId}`);
                }}
                className="px-3 whitespace-nowrap"
              >
                Generate
              </Button>
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Interview Notes
          </label>
          <textarea
            rows={3}
            placeholder="Add any special instructions or notes for this interview..."
            className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
            value={formData?.notes}
            onChange={(e) => handleInputChange('notes', e?.target?.value)}
          />
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <div className="flex flex-col items-end">
            <Button
              type="submit"
              loading={isLoading}
              disabled={!isFormValid()}
              iconName="Calendar"
              iconPosition="left"
            >
              {editingInterview ? 'Update Interview' : 'Schedule Interview'}
            </Button>
            {!isFormValid() && (
              <p className="text-xs text-muted-foreground mt-1">
                {!formData?.candidateId ? 'Select a candidate' :
                 !formData?.interviewerId ? 'Select an interviewer' :
                 !formData?.date ? 'Choose a date' :
                 !formData?.time ? 'Choose a time' :
                 formData?.type === 'video' && !formData?.meetingLink?.trim() ? 'Add meeting link' :
                 formData?.type === 'in-person' && !formData?.location?.trim() ? 'Add location' :
                 'Complete all required fields'}
              </p>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default SchedulingForm;